import { useState, useEffect, useMemo } from 'react';
import Layout from '../components/Layout';
import { api } from '../lib/api';

// Types for the group-based tree
interface BlueprintInfo {
  typeId: number;
  nameEn: string;
  iconId: number | null;
}

interface GroupNode {
  id: number;
  nameEn: string;
  blueprintCount: number;
  blueprints: BlueprintInfo[];
}

interface BlueprintProduct {
  typeId: number;
  nameEn: string;
  iconId: number | null;
  quantity: number;
  activity: string;
}

interface BlueprintMaterial {
  typeId: number;
  nameEn: string;
  iconId: number | null;
  quantity: number;
  activity: string;
}

interface BlueprintDetails {
  typeId: number;
  nameEn: string;
  iconId: number | null;
  description: any;
  marketGroupId: number | null;
  blueprintData: {
    id: number;
    maxProductionLimit: number;
    activities: any;
    products: BlueprintProduct[];
    materials: BlueprintMaterial[];
  } | null;
}

interface Character {
  characterId: string;
  characterName: string;
  corporationId: string | null;
  corporationName: string | null;
  allianceId: string | null;
  allianceName: string | null;
  syncStatus: 'never' | 'success' | 'failed' | 'in_progress';
  lastSyncAt: string | null;
  itemCount: number | null;
  errorMessage: string | null;
}

interface OwnedBlueprint {
  itemId: string;
  characterId: string;
  characterName: string;
  locationId: string;
  locationName: string | null;
  solarSystemName: string | null;
  locationFlag: string;
  isOriginal: boolean;
  isCopy: boolean;
  runs: number;
  materialEfficiency: number;
  timeEfficiency: number;
}

function formatTimeAgo(dateString: string | null): string {
  if (!dateString) return 'Never';
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  return `${diffDays}d ago`;
}

// Tree connector component for groups (Level 0)
function GroupTreeConnector({
  isLast,
  hasChildren,
  isExpanded,
  onToggle,
}: {
  isLast: boolean;
  hasChildren: boolean;
  isExpanded: boolean;
  onToggle: () => void;
}) {
  return (
    <div className="relative flex-shrink-0" style={{ width: '20px', height: '40px' }}>
      <svg width="20" height="40" className="absolute inset-0">
        {/* Vertical line */}
        <line
          x1="8"
          y1="0"
          x2="8"
          y2={isLast ? '20' : '40'}
          stroke="#4B5563"
          strokeWidth="1"
        />
        {/* Horizontal line */}
        <line x1="8" y1="20" x2="20" y2="20" stroke="#4B5563" strokeWidth="1" />
      </svg>
      {/* Expand/collapse button */}
      {hasChildren && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onToggle();
          }}
          className="absolute top-1/2 left-0.5 transform -translate-y-1/2 w-4 h-4 bg-gray-700 rounded border border-gray-600 flex items-center justify-center text-gray-400 hover:bg-gray-600 hover:text-white transition-colors z-10"
        >
          <svg
            className={`w-2.5 h-2.5 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path
              fillRule="evenodd"
              d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
              clipRule="evenodd"
            />
          </svg>
        </button>
      )}
    </div>
  );
}

export default function Blueprints() {
  const [groups, setGroups] = useState<GroupNode[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set());
  const [selectedNodeId, setSelectedNodeId] = useState<string>('summary');
  const [selectedBlueprint, setSelectedBlueprint] = useState<BlueprintDetails | null>(null);
  const [loadingBlueprint, setLoadingBlueprint] = useState(false);

  // Character blueprint sync state
  const [characters, setCharacters] = useState<Character[]>([]);
  const [loadingCharacters, setLoadingCharacters] = useState(true);
  const [refreshingAll, setRefreshingAll] = useState(false);
  const [refreshProgress, setRefreshProgress] = useState(0);
  const [refreshTotal, setRefreshTotal] = useState(0);

  // Owned blueprints state
  const [ownedCounts, setOwnedCounts] = useState<Record<number, number>>({});
  const [bpoCounts, setBpoCounts] = useState<Record<number, number>>({});
  const [bpcCounts, setBpcCounts] = useState<Record<number, number>>({});
  const [showOwnedOnly, setShowOwnedOnly] = useState(false);
  const [blueprintTypeFilter, setBlueprintTypeFilter] = useState<'all' | 'bpo' | 'bpc'>('all');
  const [ownedBlueprints, setOwnedBlueprints] = useState<OwnedBlueprint[]>([]);
  const [loadingOwned, setLoadingOwned] = useState(false);

  // Search state
  const [searchQuery, setSearchQuery] = useState('');

  // Fetch groups on mount
  useEffect(() => {
    const fetchGroups = async () => {
      try {
        setLoading(true);
        const response = await api.get('/market-groups/blueprints');
        setGroups(response.data.groups);
      } catch (err: any) {
        setError(err.response?.data?.error || 'Failed to load blueprints');
      } finally {
        setLoading(false);
      }
    };

    fetchGroups();
  }, []);

  // Fetch characters and owned counts on mount
  useEffect(() => {
    const fetchCharactersAndOwned = async () => {
      try {
        setLoadingCharacters(true);
        const [charsRes, ownedRes] = await Promise.all([
          api.get('/character-blueprints/characters'),
          api.get('/character-blueprints/owned-counts'),
        ]);
        setCharacters(charsRes.data.characters);
        setOwnedCounts(ownedRes.data.ownedCounts);
        setBpoCounts(ownedRes.data.bpoCounts || {});
        setBpcCounts(ownedRes.data.bpcCounts || {});
      } catch (err: any) {
        console.error('Failed to load character data:', err);
      } finally {
        setLoadingCharacters(false);
      }
    };

    fetchCharactersAndOwned();
  }, []);

  // Fetch blueprint details and owned info when a blueprint is selected
  useEffect(() => {
    if (selectedNodeId.startsWith('bp-')) {
      const typeId = parseInt(selectedNodeId.replace('bp-', ''));
      fetchBlueprintDetails(typeId);
      fetchOwnedDetails(typeId);
    } else {
      setSelectedBlueprint(null);
      setOwnedBlueprints([]);
    }
  }, [selectedNodeId]);

  const fetchBlueprintDetails = async (typeId: number) => {
    try {
      setLoadingBlueprint(true);
      const response = await api.get(`/market-groups/blueprint/${typeId}`);
      setSelectedBlueprint(response.data.blueprint);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load blueprint details');
    } finally {
      setLoadingBlueprint(false);
    }
  };

  const fetchOwnedDetails = async (typeId: number) => {
    try {
      setLoadingOwned(true);
      const response = await api.get(`/character-blueprints/owned/${typeId}`);
      setOwnedBlueprints(response.data.blueprints);
    } catch (err: any) {
      console.error('Failed to load owned blueprint details:', err);
      setOwnedBlueprints([]);
    } finally {
      setLoadingOwned(false);
    }
  };

  const toggleExpand = (nodeId: string) => {
    setExpandedNodes((prev) => {
      const next = new Set(prev);
      if (next.has(nodeId)) {
        next.delete(nodeId);
      } else {
        next.add(nodeId);
      }
      return next;
    });
  };

  // Refresh all characters sequentially
  const handleRefreshAll = async () => {
    if (refreshingAll || characters.length === 0) return;

    setRefreshingAll(true);
    setRefreshProgress(0);
    setRefreshTotal(characters.length);

    for (let i = 0; i < characters.length; i++) {
      const character = characters[i];
      setRefreshProgress(i);

      try {
        await api.post(`/character-blueprints/character/${character.characterId}/sync`);
        // Update the character in the list
        const charsRes = await api.get('/character-blueprints/characters');
        setCharacters(charsRes.data.characters);
      } catch (error: any) {
        console.error(`Failed to sync ${character.characterName}:`, error);
        // Update character with failed status but continue with others
        setCharacters(prev => prev.map(c =>
          c.characterId === character.characterId
            ? { ...c, syncStatus: 'failed', errorMessage: error.response?.data?.message || 'Sync failed' }
            : c
        ));
      }
    }

    // Refresh owned counts after sync
    try {
      const ownedRes = await api.get('/character-blueprints/owned-counts');
      setOwnedCounts(ownedRes.data.ownedCounts);
      setBpoCounts(ownedRes.data.bpoCounts || {});
      setBpcCounts(ownedRes.data.bpcCounts || {});
    } catch (err) {
      console.error('Failed to refresh owned counts:', err);
    }

    setRefreshProgress(characters.length);
    setRefreshingAll(false);
  };

  // Calculate oldest last sync time across all characters
  const oldestSyncTime = useMemo(() => {
    const syncTimes = characters
      .filter(c => c.lastSyncAt)
      .map(c => new Date(c.lastSyncAt!).getTime());

    if (syncTimes.length === 0) return null;
    if (syncTimes.length < characters.length) return null; // Some characters never synced

    const oldest = Math.min(...syncTimes);
    return new Date(oldest).toISOString();
  }, [characters]);

  // Calculate total unique owned blueprints (number of different blueprint types)
  const totalOwnedUnique = useMemo(() => {
    return Object.keys(ownedCounts).length;
  }, [ownedCounts]);

  // Calculate total owned blueprint copies
  const totalOwnedCopies = useMemo(() => {
    return Object.values(ownedCounts).reduce((sum, count) => sum + count, 0);
  }, [ownedCounts]);

  // Calculate unique owned count for a group (count of unique blueprint types owned)
  // Takes into account the blueprintTypeFilter
  const getGroupOwnedCount = (group: GroupNode) => {
    return group.blueprints.filter(bp => {
      if (blueprintTypeFilter === 'bpo') {
        return (bpoCounts[bp.typeId] || 0) > 0;
      } else if (blueprintTypeFilter === 'bpc') {
        return (bpcCounts[bp.typeId] || 0) > 0;
      }
      return ownedCounts[bp.typeId] > 0;
    }).length;
  };

  // Get BPO count for a group
  const getGroupBPOCount = (group: GroupNode) => {
    return group.blueprints.filter(bp => (bpoCounts[bp.typeId] || 0) > 0).length;
  };

  // Get BPC count for a group
  const getGroupBPCCount = (group: GroupNode) => {
    return group.blueprints.filter(bp => (bpcCounts[bp.typeId] || 0) > 0).length;
  };

  // Helper to check if a blueprint matches the ownership filter
  const blueprintMatchesOwnershipFilter = (typeId: number) => {
    if (blueprintTypeFilter === 'bpo') {
      return (bpoCounts[typeId] || 0) > 0;
    } else if (blueprintTypeFilter === 'bpc') {
      return (bpcCounts[typeId] || 0) > 0;
    }
    return ownedCounts[typeId] > 0;
  };

  // Filter groups based on owned filter and search
  const filteredGroups = useMemo(() => {
    let result = groups;

    // Filter by owned if enabled - also filter level 2 (individual blueprints)
    if (showOwnedOnly) {
      result = result.map(group => {
        // Filter blueprints to only show owned ones (respecting BPO/BPC filter)
        const ownedBlueprints = group.blueprints.filter(bp =>
          blueprintMatchesOwnershipFilter(bp.typeId)
        );

        if (ownedBlueprints.length === 0) {
          return null;
        }

        return {
          ...group,
          blueprints: ownedBlueprints,
          blueprintCount: ownedBlueprints.length,
        };
      }).filter((g): g is GroupNode => g !== null);
    }

    // Filter by search query (minimum 3 characters)
    if (searchQuery.length >= 3) {
      const query = searchQuery.toLowerCase();
      result = result.map(group => {
        // Check if group name matches
        const groupMatches = group.nameEn.toLowerCase().includes(query);

        // Filter blueprints that match
        const matchingBlueprints = group.blueprints.filter(bp =>
          bp.nameEn.toLowerCase().includes(query)
        );

        // Include group if group name matches or has matching blueprints
        if (groupMatches || matchingBlueprints.length > 0) {
          return {
            ...group,
            blueprints: groupMatches ? group.blueprints : matchingBlueprints,
            blueprintCount: groupMatches ? group.blueprintCount : matchingBlueprints.length,
          };
        }
        return null;
      }).filter((g): g is GroupNode => g !== null);
    }

    return result;
  }, [groups, showOwnedOnly, blueprintTypeFilter, searchQuery, ownedCounts, bpoCounts, bpcCounts]);

  // Count total blueprints
  const totalBlueprints = groups.reduce((sum, g) => sum + g.blueprintCount, 0);

  // Render a group node (Level 0)
  const renderGroupNode = (
    node: GroupNode,
    _index: number,
    isLastChild: boolean
  ): React.ReactNode => {
    const nodeId = `grp-${node.id}`;
    const isSelected = selectedNodeId === nodeId;
    const isExpanded = expandedNodes.has(nodeId);
    const hasChildren = node.blueprints.length > 0;
    const bpoCount = getGroupBPOCount(node);
    const bpcCount = getGroupBPCCount(node);
    const hasAnyOwned = bpoCount > 0 || bpcCount > 0;

    return (
      <div key={nodeId}>
        {/* Group row */}
        <div
          className={`group flex items-center gap-1 cursor-pointer transition-colors rounded ${
            isSelected ? 'bg-blue-600' : 'hover:bg-gray-700'
          }`}
          style={{ paddingRight: '8px' }}
          onClick={() => setSelectedNodeId(nodeId)}
        >
          {/* Tree connector */}
          <GroupTreeConnector
            isLast={isLastChild}
            hasChildren={hasChildren}
            isExpanded={isExpanded}
            onToggle={() => toggleExpand(nodeId)}
          />

          {/* Icon - use local icon for groups */}
          <div className="relative flex-shrink-0">
            <img
              src="/eve-icons/2703.png"
              alt={node.nameEn}
              className="w-8 h-8 rounded"
            />
          </div>

          {/* Name */}
          <span className={`text-sm truncate ml-1 ${isSelected ? 'text-white font-medium' : 'text-gray-300'}`}>
            {node.nameEn}
          </span>

          {/* BPO count badge (green) */}
          {bpoCount > 0 && (
            <span className="ml-auto text-xs text-green-400 bg-green-900/50 px-1.5 py-0.5 rounded">
              {bpoCount}
            </span>
          )}

          {/* BPC count badge (yellow) */}
          {bpcCount > 0 && (
            <span className={`${bpoCount > 0 ? 'ml-1' : 'ml-auto'} text-xs text-yellow-300 bg-yellow-900/50 px-1.5 py-0.5 rounded`}>
              {bpcCount}
            </span>
          )}

          {/* Blueprint count badge */}
          <span className={`${hasAnyOwned ? 'ml-1' : 'ml-auto'} text-xs text-gray-500 bg-gray-700 px-2 py-0.5 rounded`}>
            {node.blueprintCount}
          </span>
        </div>

        {/* Blueprints (Level 1) */}
        {isExpanded && (
          <>
            {node.blueprints.map((bp, bpIndex) => {
              const bpNodeId = `bp-${bp.typeId}`;
              const isBpSelected = selectedNodeId === bpNodeId;
              const isBpLast = bpIndex === node.blueprints.length - 1;
              const bpBPOCount = bpoCounts[bp.typeId] || 0;
              const bpBPCCount = bpcCounts[bp.typeId] || 0;
              const isOwned = bpBPOCount > 0 || bpBPCCount > 0;

              return (
                <div
                  key={bpNodeId}
                  className={`group flex items-center gap-1 cursor-pointer transition-colors rounded ${
                    isBpSelected
                      ? 'bg-blue-600'
                      : isOwned
                        ? 'bg-green-900/40 hover:bg-green-900/60'
                        : 'hover:bg-gray-700'
                  }`}
                  style={{ paddingRight: '8px' }}
                  onClick={() => setSelectedNodeId(bpNodeId)}
                >
                  {/* Ancestor vertical line for group */}
                  <div className="relative flex-shrink-0" style={{ width: '20px', height: '40px' }}>
                    {!isLastChild && (
                      <svg width="20" height="40" className="absolute inset-0">
                        <line x1="8" y1="0" x2="8" y2="40" stroke="#4B5563" strokeWidth="1" />
                      </svg>
                    )}
                  </div>

                  {/* Tree connector (no expand button for blueprints) */}
                  <div className="relative flex-shrink-0" style={{ width: '20px', height: '40px' }}>
                    <svg width="20" height="40" className="absolute inset-0">
                      <line x1="8" y1="0" x2="8" y2={isBpLast ? '20' : '40'} stroke="#4B5563" strokeWidth="1" />
                      <line x1="8" y1="20" x2="20" y2="20" stroke="#4B5563" strokeWidth="1" />
                    </svg>
                  </div>

                  {/* Blueprint Icon (use typeId) */}
                  <div className="relative flex-shrink-0">
                    <img
                      src={`https://images.evetech.net/types/${bp.typeId}/bp?size=32`}
                      alt={bp.nameEn}
                      className="w-8 h-8 rounded"
                      onError={(e) => {
                        e.currentTarget.style.display = 'none';
                      }}
                    />
                  </div>

                  {/* Name */}
                  <span className={`text-sm truncate ml-1 flex-1 ${isBpSelected ? 'text-white font-medium' : 'text-gray-300'}`}>
                    {bp.nameEn}
                  </span>

                  {/* BPO count badge (green) */}
                  {bpBPOCount > 0 && (
                    <span className="text-xs text-green-400 bg-green-900/50 px-1.5 py-0.5 rounded font-medium">
                      {bpBPOCount}
                    </span>
                  )}

                  {/* BPC count badge (yellow) */}
                  {bpBPCCount > 0 && (
                    <span className="text-xs text-yellow-300 bg-yellow-900/60 px-1.5 py-0.5 rounded font-medium">
                      {bpBPCCount}
                    </span>
                  )}
                </div>
              );
            })}
          </>
        )}
      </div>
    );
  };

  return (
    <Layout>
      <div className="h-full flex">
        {/* Left Panel - Tree */}
        <div className="w-96 bg-gray-800 border-r border-gray-700 flex flex-col">
          {/* Header */}
          <div className="p-4 border-b border-gray-700">
            <h2 className="text-lg font-semibold text-white">Blueprints</h2>
            <p className="text-sm text-gray-400 mt-1">
              {loading ? 'Loading...' : `${totalBlueprints.toLocaleString()} blueprints in ${groups.length} groups`}
            </p>
          </div>

          {/* Search */}
          <div className="px-4 py-2 border-b border-gray-700">
            <div className="relative">
              <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                type="text"
                placeholder="Search blueprints (min 3 chars)..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-blue-500 text-sm"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>
          </div>

          {/* Filters */}
          <div className="px-4 py-2 border-b border-gray-700 space-y-2">
            {/* Show owned only checkbox */}
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={showOwnedOnly}
                onChange={(e) => setShowOwnedOnly(e.target.checked)}
                className="w-4 h-4 rounded border-gray-500 bg-gray-700 text-green-500 focus:ring-green-500 focus:ring-offset-0"
              />
              <span className="text-sm text-gray-300">Show owned only</span>
              {totalOwnedUnique > 0 && (
                <span className="text-xs text-green-400 bg-green-900/50 px-1.5 py-0.5 rounded ml-auto">
                  {totalOwnedUnique} owned
                </span>
              )}
            </label>

            {/* BPO/BPC filter button group */}
            <div className="flex rounded-lg overflow-hidden border border-gray-600">
              <button
                onClick={() => setBlueprintTypeFilter('all')}
                className={`flex-1 px-3 py-1.5 text-xs font-medium transition-colors ${
                  blueprintTypeFilter === 'all'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                }`}
              >
                All
              </button>
              <button
                onClick={() => setBlueprintTypeFilter('bpo')}
                className={`flex-1 px-3 py-1.5 text-xs font-medium transition-colors border-l border-gray-600 ${
                  blueprintTypeFilter === 'bpo'
                    ? 'bg-green-600 text-white'
                    : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                }`}
              >
                BPO only
              </button>
              <button
                onClick={() => setBlueprintTypeFilter('bpc')}
                className={`flex-1 px-3 py-1.5 text-xs font-medium transition-colors border-l border-gray-600 ${
                  blueprintTypeFilter === 'bpc'
                    ? 'bg-yellow-600 text-white'
                    : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                }`}
              >
                BPC only
              </button>
            </div>
          </div>

          {/* Tree */}
          <div className="flex-1 overflow-y-auto p-2">
            {error && (
              <div className="p-4 bg-red-900/50 border border-red-700 rounded-lg mb-4">
                <p className="text-red-400 text-sm">{error}</p>
              </div>
            )}

            {loading ? (
              <div className="flex items-center justify-center h-32">
                <svg className="animate-spin h-8 w-8 text-blue-500" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              </div>
            ) : (
              <>
                {/* Blueprint Summary Root Node */}
                <div
                  className={`flex items-center gap-2 cursor-pointer transition-colors rounded p-2 ${
                    selectedNodeId === 'summary' ? 'bg-blue-600' : 'hover:bg-gray-700'
                  }`}
                  onClick={() => setSelectedNodeId('summary')}
                >
                  <div className="w-8 h-8 rounded bg-blue-600 flex items-center justify-center">
                    <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                  <span className={`text-sm font-medium ${selectedNodeId === 'summary' ? 'text-white' : 'text-gray-300'}`}>
                    Blueprint Summary
                  </span>
                </div>

                {/* Search results info */}
                {searchQuery.length >= 3 && (
                  <div className="px-2 py-1 text-xs text-gray-400">
                    Found {filteredGroups.reduce((sum, g) => sum + g.blueprintCount, 0)} blueprints in {filteredGroups.length} groups
                  </div>
                )}

                {/* Groups Tree */}
                {filteredGroups.map((group, index) => (
                  <div key={`grp-${group.id}`}>
                    {renderGroupNode(group, index, index === filteredGroups.length - 1)}
                  </div>
                ))}

                {showOwnedOnly && filteredGroups.length === 0 && searchQuery.length < 3 && (
                  <div className="text-center text-gray-400 py-8">
                    <p>No owned blueprints found.</p>
                    <p className="text-sm mt-1">Sync your characters to see owned blueprints.</p>
                  </div>
                )}

                {searchQuery.length >= 3 && filteredGroups.length === 0 && (
                  <div className="text-center text-gray-400 py-8">
                    <p>No blueprints found for "{searchQuery}"</p>
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* Right Panel - Details */}
        <div className="flex-1 bg-gray-900 overflow-y-auto">
          {selectedNodeId === 'summary' ? (
            // Summary View
            <div className="p-6">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-16 h-16 rounded-lg bg-blue-600 flex items-center justify-center">
                  <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-white">Blueprint Summary</h1>
                  <p className="text-sm text-gray-400 mt-1">
                    Browse and explore EVE Online blueprints by product group
                  </p>
                </div>
              </div>

              {/* Characters Section */}
              <div className="grid grid-cols-3 gap-4 mb-6">
                <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
                  <div className="text-gray-400 text-sm">Characters</div>
                  <div className="text-2xl font-bold text-white">
                    {loadingCharacters ? '...' : characters.length}
                  </div>
                </div>
                <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
                  <div className="text-gray-400 text-sm">Last Refreshed</div>
                  <div className="text-2xl font-bold text-white">
                    {loadingCharacters ? '...' : formatTimeAgo(oldestSyncTime)}
                  </div>
                  {!loadingCharacters && !oldestSyncTime && characters.length > 0 && (
                    <div className="text-xs text-gray-500">Some characters never synced</div>
                  )}
                </div>
                <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
                  <div className="text-gray-400 text-sm mb-2">Refresh All</div>
                  {refreshingAll ? (
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <svg className="w-4 h-4 animate-spin text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        </svg>
                        <span className="text-sm text-gray-300">
                          {refreshProgress + 1}/{refreshTotal}
                        </span>
                        <span className="text-sm text-gray-400 ml-auto">
                          {Math.round(((refreshProgress + 1) / refreshTotal) * 100)}%
                        </span>
                      </div>
                      <div className="w-full bg-gray-700 rounded-full h-2">
                        <div
                          className="bg-blue-500 h-2 rounded-full transition-all duration-300"
                          style={{ width: `${((refreshProgress + 1) / refreshTotal) * 100}%` }}
                        />
                      </div>
                      <div className="text-xs text-gray-500 mt-1">
                        Syncing {characters[refreshProgress]?.characterName || '...'}
                      </div>
                    </div>
                  ) : (
                    <button
                      onClick={handleRefreshAll}
                      disabled={loadingCharacters || characters.length === 0}
                      className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white text-sm rounded-lg transition-colors"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      </svg>
                      Refresh All Characters
                    </button>
                  )}
                </div>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-3 gap-4 mb-6">
                <div className="bg-gray-800 rounded-lg p-4">
                  <div className="text-3xl font-bold text-white">{totalBlueprints.toLocaleString()}</div>
                  <div className="text-sm text-gray-400">Total Blueprints</div>
                </div>
                <div className="bg-gray-800 rounded-lg p-4">
                  <div className="text-3xl font-bold text-white">{groups.length}</div>
                  <div className="text-sm text-gray-400">Product Groups</div>
                </div>
                <div className="bg-gray-800 rounded-lg p-4">
                  <div className="text-3xl font-bold text-green-400">{totalOwnedUnique.toLocaleString()}</div>
                  <div className="text-sm text-gray-400">Owned Types ({totalOwnedCopies} copies)</div>
                </div>
              </div>

              {/* Group List */}
              <div className="bg-gray-800 rounded-lg p-4">
                <h3 className="text-lg font-semibold text-white mb-4">Groups</h3>
                <div className="grid grid-cols-2 gap-2 max-h-96 overflow-y-auto">
                  {groups.slice(0, 50).map((group) => {
                    const ownedCount = getGroupOwnedCount(group);
                    return (
                      <div
                        key={group.id}
                        className="flex items-center gap-3 p-3 bg-gray-700 rounded-lg cursor-pointer hover:bg-gray-600 transition-colors"
                        onClick={() => {
                          setSelectedNodeId(`grp-${group.id}`);
                          setExpandedNodes((prev) => new Set([...prev, `grp-${group.id}`]));
                        }}
                      >
                        <img
                          src="/eve-icons/2703.png"
                          alt={group.nameEn}
                          className="w-10 h-10 rounded"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-white truncate">{group.nameEn}</div>
                          <div className="text-xs text-gray-400">
                            {group.blueprintCount} blueprints
                            {ownedCount > 0 && (
                              <span className="text-green-400 ml-2">({ownedCount} owned)</span>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
                {groups.length > 50 && (
                  <p className="text-sm text-gray-400 mt-4 text-center">
                    Showing 50 of {groups.length} groups. Use the tree to explore all.
                  </p>
                )}
              </div>
            </div>
          ) : selectedNodeId.startsWith('grp-') ? (
            // Group View
            <div className="p-6">
              {(() => {
                const groupId = parseInt(selectedNodeId.replace('grp-', ''));
                const group = groups.find(g => g.id === groupId);

                if (!group) {
                  return <div className="text-gray-400">Group not found</div>;
                }

                const ownedCount = getGroupOwnedCount(group);

                return (
                  <>
                    <div className="flex items-center gap-4 mb-6">
                      <img
                        src="/eve-icons/2703.png"
                        alt={group.nameEn}
                        className="w-16 h-16 rounded-lg"
                      />
                      <div>
                        <h1 className="text-2xl font-bold text-white">{group.nameEn}</h1>
                        <p className="text-sm text-gray-400 mt-1">
                          {group.blueprintCount} blueprints
                          {ownedCount > 0 && (
                            <span className="text-green-400 ml-2">({ownedCount} owned)</span>
                          )}
                        </p>
                      </div>
                    </div>

                    {/* Blueprints in this group */}
                    <div className="bg-gray-800 rounded-lg p-4">
                      <h3 className="text-lg font-semibold text-white mb-4">Blueprints</h3>
                      <div className="grid grid-cols-2 gap-2">
                        {group.blueprints.map((bp) => {
                          const bpOwnedCount = ownedCounts[bp.typeId] || 0;
                          return (
                            <div
                              key={bp.typeId}
                              className="flex items-center gap-3 p-3 bg-gray-700 rounded-lg cursor-pointer hover:bg-gray-600 transition-colors"
                              onClick={() => setSelectedNodeId(`bp-${bp.typeId}`)}
                            >
                              <img
                                src={`https://images.evetech.net/types/${bp.typeId}/bp?size=32`}
                                alt={bp.nameEn}
                                className="w-10 h-10 rounded"
                                onError={(e) => {
                                  e.currentTarget.style.display = 'none';
                                }}
                              />
                              <div className="flex-1 min-w-0">
                                <div className="text-sm font-medium text-white truncate">{bp.nameEn}</div>
                                {bpOwnedCount > 0 && (
                                  <div className="text-xs text-green-400">{bpOwnedCount} owned</div>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </>
                );
              })()}
            </div>
          ) : selectedNodeId.startsWith('bp-') ? (
            // Blueprint Detail View
            <div className="p-6">
              {loadingBlueprint ? (
                <div className="flex items-center justify-center h-32">
                  <svg className="animate-spin h-8 w-8 text-blue-500" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                </div>
              ) : selectedBlueprint ? (
                <>
                  <div className="flex items-center gap-4 mb-6">
                    <img
                      src={`https://images.evetech.net/types/${selectedBlueprint.typeId}/bp?size=64`}
                      alt={selectedBlueprint.nameEn || ''}
                      className="w-16 h-16 rounded-lg"
                      onError={(e) => {
                        e.currentTarget.style.display = 'none';
                      }}
                    />
                    <div>
                      <h1 className="text-2xl font-bold text-white">{selectedBlueprint.nameEn}</h1>
                      <p className="text-sm text-gray-400 mt-1">
                        Type ID: {selectedBlueprint.typeId}
                        {ownedBlueprints.length > 0 && (
                          <span className="text-green-400 ml-2">({ownedBlueprints.length} owned)</span>
                        )}
                      </p>
                    </div>
                  </div>

                  {/* Owned Blueprints Section */}
                  {ownedBlueprints.length > 0 && (
                    <div className="bg-gray-800 rounded-lg p-4 mb-4">
                      <h3 className="text-lg font-semibold text-white mb-4">Owned Copies</h3>
                      {loadingOwned ? (
                        <div className="flex items-center justify-center h-16">
                          <svg className="animate-spin h-6 w-6 text-blue-500" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                        </div>
                      ) : (
                        <div className="overflow-x-auto">
                          <table className="w-full">
                            <thead>
                              <tr className="text-left text-xs text-gray-400 uppercase border-b border-gray-700">
                                <th className="pb-2 pr-4">Owner</th>
                                <th className="pb-2 pr-4">Type</th>
                                <th className="pb-2 pr-4">System</th>
                                <th className="pb-2 pr-4">Location</th>
                                <th className="pb-2 pr-4 text-center">ME</th>
                                <th className="pb-2 pr-4 text-center">TE</th>
                                <th className="pb-2 text-center">Runs</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-700">
                              {ownedBlueprints.map((bp) => (
                                <tr key={bp.itemId} className="text-sm">
                                  <td className="py-2 pr-4">
                                    <div className="flex items-center gap-2">
                                      <img
                                        src={`https://images.evetech.net/characters/${bp.characterId}/portrait?size=32`}
                                        alt={bp.characterName}
                                        className="w-6 h-6 rounded"
                                      />
                                      <span className="text-white">{bp.characterName}</span>
                                    </div>
                                  </td>
                                  <td className="py-2 pr-4">
                                    {bp.isOriginal ? (
                                      <span className="text-blue-400 bg-blue-900/30 px-2 py-0.5 rounded text-xs">BPO</span>
                                    ) : (
                                      <span className="text-orange-400 bg-orange-900/30 px-2 py-0.5 rounded text-xs">BPC</span>
                                    )}
                                  </td>
                                  <td className="py-2 pr-4 text-yellow-400">
                                    {bp.solarSystemName || '-'}
                                  </td>
                                  <td className="py-2 pr-4 text-gray-400 max-w-[200px] truncate" title={bp.locationName || bp.locationId}>
                                    {bp.locationName || `ID: ${bp.locationId}`}
                                  </td>
                                  <td className="py-2 pr-4 text-center">
                                    <span className={`font-mono ${bp.materialEfficiency >= 10 ? 'text-green-400' : 'text-white'}`}>
                                      {bp.materialEfficiency}%
                                    </span>
                                  </td>
                                  <td className="py-2 pr-4 text-center">
                                    <span className={`font-mono ${bp.timeEfficiency >= 20 ? 'text-green-400' : 'text-white'}`}>
                                      {bp.timeEfficiency}%
                                    </span>
                                  </td>
                                  <td className="py-2 text-center text-white font-mono">
                                    {bp.isOriginal ? '∞' : bp.runs}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  )}

                  {selectedBlueprint.blueprintData && (
                    <>
                      {/* Products */}
                      {selectedBlueprint.blueprintData.products.length > 0 && (
                        <div className="bg-gray-800 rounded-lg p-4 mb-4">
                          <h3 className="text-lg font-semibold text-white mb-4">Products</h3>
                          <div className="space-y-2">
                            {selectedBlueprint.blueprintData.products.map((product) => (
                              <div
                                key={`${product.typeId}-${product.activity}`}
                                className="flex items-center gap-3 p-3 bg-gray-700 rounded-lg"
                              >
                                <img
                                  src={`https://images.evetech.net/types/${product.typeId}/icon?size=32`}
                                  alt={product.nameEn || ''}
                                  className="w-10 h-10 rounded"
                                  onError={(e) => {
                                    e.currentTarget.style.display = 'none';
                                  }}
                                />
                                <div className="flex-1">
                                  <div className="text-sm font-medium text-white">{product.nameEn}</div>
                                  <div className="text-xs text-gray-400">
                                    Activity: {product.activity} | Quantity: {product.quantity}
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Materials */}
                      {selectedBlueprint.blueprintData.materials.length > 0 && (
                        <div className="bg-gray-800 rounded-lg p-4">
                          <h3 className="text-lg font-semibold text-white mb-4">Materials</h3>
                          <div className="space-y-2">
                            {selectedBlueprint.blueprintData.materials
                              .filter((m) => m.activity === 'manufacturing' || m.activity === 'reaction')
                              .map((material) => (
                                <div
                                  key={`${material.typeId}-${material.activity}`}
                                  className="flex items-center gap-3 p-3 bg-gray-700 rounded-lg"
                                >
                                  <img
                                    src={`https://images.evetech.net/types/${material.typeId}/icon?size=32`}
                                    alt={material.nameEn || ''}
                                    className="w-10 h-10 rounded"
                                    onError={(e) => {
                                      e.currentTarget.style.display = 'none';
                                    }}
                                  />
                                  <div className="flex-1">
                                    <div className="text-sm font-medium text-white">{material.nameEn}</div>
                                    <div className="text-xs text-gray-400">
                                      Quantity: {material.quantity.toLocaleString()}
                                    </div>
                                  </div>
                                </div>
                              ))}
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </>
              ) : (
                <div className="text-gray-400">Blueprint not found</div>
              )}
            </div>
          ) : (
            <div className="p-6 text-gray-400">Select an item from the tree</div>
          )}
        </div>
      </div>
    </Layout>
  );
}
